import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { langClass } from "@/i18n/langUtils";
import { useT } from "@/i18n/LanguageContext";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import SectionHeading from "@/components/SectionHeading";
import TrustScore from "@/components/TrustScore";
import api from "@/lib/api";
import { asArray } from "@/lib/safeData";
import { Search, BadgeCheck, AlertTriangle, ChevronRight, MapPin } from "lucide-react";

const CATEGORIES = [
  "Stock Market Education", "Technical Analysis", "Long-term Investing",
  "Intraday Trading", "Macro & Sectoral", "Algo & Quant",
];

const Mentors = () => {
  const { t, lang } = useT();
  const m = t.mentors || {};
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [verified, setVerified] = useState("all");
  const [sort, setSort] = useState("trust");
  const langCls = langClass(lang);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort };
      if (q) params.q = q;
      if (category !== "all") params.category = category;
      if (verified !== "all") params.verified = verified === "verified";
      const res = await api.get("/mentors", { params });
      setItems(asArray(res.data));
    } finally {
      setLoading(false);
    }
  }, [q, category, verified, sort]);

  const isFirstLoad = useRef(true);
  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      void load();
      return;
    }
    const id = setTimeout(() => void load(), 250);
    return () => clearTimeout(id);
  }, [load]);

  const fieldCls = "bg-white/[0.03] border-white/15 text-white";

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-8 py-16 md:py-24" data-testid="mentors-page">
      <SectionHeading eyebrow={m.eyebrow} title={m.title} subtitle={m.subtitle} />

      <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-5 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input
            placeholder={m.search}
            value={q} onChange={(e) => setQ(e.target.value)}
            className={`pl-10 h-12 ${fieldCls}`}
            data-testid="mentors-search"
          />
        </div>
        <div className="md:col-span-3">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className={`h-12 ${fieldCls}`} data-testid="mentors-category">
              <SelectValue placeholder={m.category} />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10 text-white">
              <SelectItem value="all">{m.allCategories}</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Select value={verified} onValueChange={setVerified}>
            <SelectTrigger className={`h-12 ${fieldCls}`} data-testid="mentors-verified">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10 text-white">
              <SelectItem value="all">{m.all}</SelectItem>
              <SelectItem value="verified">{m.verifiedOnly}</SelectItem>
              <SelectItem value="unverified">{m.unverified}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className={`h-12 ${fieldCls}`} data-testid="mentors-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10 text-white">
              <SelectItem value="trust">{m.sortTrust}</SelectItem>
              <SelectItem value="rating">{m.sortRating}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="mentors-grid">
        {loading && <div className={`col-span-3 text-center py-12 text-white/40 ${langCls}`}>{m.loading || t.common.loading}</div>}
        {!loading && items.length === 0 && (
          <div className={`col-span-3 surface-card p-10 text-center text-white/55 ${langCls}`}>{m.noResults}</div>
        )}
        {items.map((mentor) => (
          <Link to={`/mentors/${mentor.slug}`} key={mentor.id} className="surface-card p-6 block group" data-testid={`mentor-card-${mentor.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display text-lg font-bold truncate">{mentor.name}</h3>
                  {mentor.is_verified && <Badge className="bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30 gap-1"><BadgeCheck className="h-3 w-3" /> {m.verified}</Badge>}
                  {mentor.is_flagged && <Badge className="bg-[#FF3B30]/15 text-[#FF6A60] border border-[#FF3B30]/30 gap-1"><AlertTriangle className="h-3 w-3" /> {m.flagged}</Badge>}
                </div>
                <div className="mt-0.5 text-xs text-white/55">{mentor.company}</div>
              </div>
              <TrustScore trust={mentor.trust} size="sm" showLabel={false} />
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-white/45 font-mono-alt">
              <span>{mentor.category}</span>
              {mentor.state && <><span>·</span><span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {mentor.state}</span></>}
            </div>

            <p className="mt-3 text-sm text-white/65 line-clamp-3 leading-relaxed">{mentor.bio}</p>

            <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
              <div className="text-xs text-white/55">
                {mentor.trust.count} {m.reviews} · ⭐ {mentor.trust.avg_rating}/5
              </div>
              <span className="text-[#D4AF37] text-xs font-mono-alt inline-flex items-center group-hover:translate-x-1 transition-transform">
                {m.view} <ChevronRight className="h-3 w-3" />
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default Mentors;
